import { useEffect } from 'react'
import { Link } from 'react-router'
import Header from '../sections/Header'
import Footer from '../sections/Footer'

export default function TermsPage() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="relative">
      <Header />
      <main className="pt-24 pb-20 bg-[#0d1117] min-h-screen">
        <div className="max-w-[720px] mx-auto px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-[#06b6d4] hover:text-[#22d3ee] transition-colors mb-8"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Home
          </Link>

          <h1 className="text-3xl font-semibold text-white mb-2">Terms of Service</h1>
          <p className="text-sm text-[#6b7280] font-mono mb-12">Last Updated: June 14, 2026</p>

          <div className="space-y-10 text-[15px] text-[#d1d5db] leading-relaxed">
            <section>
              <h2 className="text-lg font-semibold text-white mb-3">1. Service Description</h2>
              <p>
                ProcurAlerts is a subscription-based software platform that aggregates publicly available
                government procurement opportunities from municipal, county, and state sources across
                New England (Massachusetts, Connecticut, Maine, New Hampshire, Rhode Island, and Vermont).
                The Service delivers bid notifications, document management tools, and related features to
                registered users.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">2. Acceptance of Terms</h2>
              <p>
                By creating an account or using ProcurAlerts, you agree to these Terms of Service. If you
                are using ProcurAlerts on behalf of a company or organization, you represent that you have
                authority to bind that entity to these terms.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">3. Accounts and Registration</h2>
              <ul className="space-y-2 list-disc list-inside marker:text-[#6b7280]">
                <li>You must provide accurate and complete registration information.</li>
                <li>You are responsible for maintaining the security of your account credentials.</li>
                <li>You must be at least 18 years old to use the Service.</li>
                <li>One person or entity per account unless on a Team plan, which permits up to 5 users under a single organization.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">4. Subscription and Billing</h2>
              <ul className="space-y-2 list-disc list-inside marker:text-[#6b7280]">
                <li>ProcurAlerts offers paid subscription plans (Pro and Team) with monthly and annual billing options.</li>
                <li>All plans include a 14-day free trial. You will not be charged until the trial period ends.</li>
                <li>Subscriptions renew automatically unless cancelled before the renewal date.</li>
                <li>You may cancel your subscription at any time through your Profile settings. Cancellation takes effect at the end of the current billing period.</li>
                <li>Refunds are not provided for partial billing periods.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">5. Acceptable Use</h2>
              <p className="mb-3">You agree not to:</p>
              <ul className="space-y-2 list-disc list-inside marker:text-[#6b7280]">
                <li>Use the Service to engage in any unlawful activity.</li>
                <li>Attempt to reverse-engineer, scrape, or redistribute data obtained through ProcurAlerts.</li>
                <li>Share account credentials with unauthorized users.</li>
                <li>Use automated tools to access the Service beyond its intended interface.</li>
                <li>Interfere with or disrupt the Service or its infrastructure.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">6. Data and Accuracy</h2>
              <ul className="space-y-2 list-disc list-inside marker:text-[#6b7280]">
                <li>ProcurAlerts aggregates publicly available procurement data. We do not guarantee the completeness, accuracy, or timeliness of any bid information.</li>
                <li>Users are responsible for independently verifying bid details, deadlines, and requirements with the issuing agency before submitting any bid or proposal.</li>
                <li>ProcurAlerts is not a substitute for direct communication with procuring entities.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">7. Intellectual Property</h2>
              <p>
                All content, design, code, and branding of ProcurAlerts are the property of ProcurAlerts
                and are protected by applicable intellectual property laws. You may not copy, modify, or
                distribute any part of the Service without written permission.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">8. Limitation of Liability</h2>
              <p className="mb-3">
                ProcurAlerts is provided "as is" without warranties of any kind, express or implied.
                To the maximum extent permitted by law:
              </p>
              <ul className="space-y-2 list-disc list-inside marker:text-[#6b7280]">
                <li>ProcurAlerts shall not be liable for any indirect, incidental, or consequential damages arising from your use of the Service.</li>
                <li>ProcurAlerts shall not be liable for missed bid opportunities, inaccurate data, or any business losses resulting from reliance on the Service.</li>
                <li>Total liability shall not exceed the amount you paid for the Service in the 12 months preceding the claim.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">9. Termination</h2>
              <p>
                We reserve the right to suspend or terminate your account at any time for violation of
                these terms, with or without notice.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">10. Modifications</h2>
              <p>
                We may update these Terms at any time. Continued use of the Service after changes
                constitutes acceptance. Material changes will be communicated via email or in-app notification.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">11. Governing Law</h2>
              <p>
                These Terms are governed by the laws of the Commonwealth of Massachusetts, without regard
                to conflict of law principles.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">12. Contact</h2>
              <p>
                For questions about these Terms, contact us at{' '}
                <a href="mailto:hello@procuralerts.com" className="text-[#06b6d4] hover:text-[#22d3ee] transition-colors">
                  hello@procuralerts.com
                </a>.
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}
